import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getTenantAllUsersRequest,
  getClientAllUsersRequest,
  getClientAllUsersSuccess,
} from "../../../../redux/clients/slice";
import {
  getInspectionFirmAllUsersRequest,
  getInspectionFirmAllUsersSuccess,
} from "../../../../redux/inspection-firms/slice";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { editIssueRequest } from "../../../../utils/APICalls";
import {
  getCreateIssueSearchOrderRequest,
  getIssueDetailsRequest,
  getOrderIssuesCountRequest,
  getOrderIssuesRequest,
} from "../../../../redux/orders/slice";
import Loading from "../../../../components/loading/loading";
import ErrorNotification from "../../../../components/error-notification/error-notification";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(name: string, selected: readonly string[], theme: Theme) {
  return {
    fontWeight: selected.includes(name)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

export default function EditIssue({
  orders,
  issueId,
  orderId,
  issueDetails,
  issueType,
  onHide,
}: any) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [searchText, setSearchText] = useState("");
  const [orderOptions, setOrderOptions] = useState<string[]>([]);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { createIssueSearchResults } = useSelector(
    (state: any) => state.orders
  );
  const { loading, tenantAllUsers, clientAllUsers } = useSelector(
    (state: any) => state.clients
  );
  const { inspectionFirmAllUsers } = useSelector(
    (state: any) => state.inspectionFirms
  );

  const validationSchema = Yup.object({
    type: Yup.string().required("This field can't be empty"),
    subType: Yup.string().required("This field can't be empty"),
    orderIds: Yup.array().min(1, "This field can't be empty"),
    tenantUserIds: Yup.array()
      .of(Yup.string())
      .when("tenantCheckbox", {
        is: (value: boolean) => value,
        then: (schema) => schema.min(1, "This field can't be empty"),
        otherwise: (schema) => schema.notRequired(),
      }),
    clientUserIds: Yup.array()
      .of(Yup.string())
      .when(["clientCheckbox", "type"], {
        is: (clientCheckbox: boolean, type: string) =>
          clientCheckbox && type === "Client",
        then: (schema) => schema.min(1, "This field can't be empty"),
        otherwise: (schema) => schema.notRequired(),
      }),
    inspectorUserIds: Yup.array()
      .of(Yup.string())
      .when(["inspectorCheckbox", "type"], {
        is: (inspectorCheckbox: boolean, type: string) =>
          inspectorCheckbox && type === "Inspector",
        then: (schema) => schema.min(1, "This field can't be empty"),
        otherwise: (schema) => schema.notRequired(),
      }),
    description: Yup.string().required("This field can't be empty"),
    priority: Yup.string().required("This field can't be empty"),
    onHoldUntilResolved: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      type: issueDetails?.type || "",
      subType: issueDetails?.subType || "",
      orderIds: [] as string[],
      tenantUserIds:
        issueDetails?.tenantUsers?.map((item: any) => item.id) ||
        ([] as string[]),
      tenantCheckbox: true,
      clientUserIds:
        issueDetails?.clientUsers?.map((item: any) => item.id) ||
        ([] as string[]),
      clientCheckbox: issueDetails?.clientUsers.length > 0 ? true : false,
      inspectorUserIds:
        issueDetails?.inspectorUsers?.map((item: any) => item.id) ||
        ([] as string[]),
      inspectorCheckbox: issueDetails?.inspectorUsers.length > 0 ? true : false,
      description: issueDetails?.description || "",
      priority: issueDetails?.priority || "",
      onHoldUntilResolved: issueDetails?.onHoldUntilResolved || false,
    },
    validationSchema,
    onSubmit: (values) => {
      const payload: any = {
        tenantUserIds: values.tenantCheckbox ? values.tenantUserIds : [],
        priority: values.priority,
        onHoldUntilResolved: values.onHoldUntilResolved,
      };
      if (values.type === "Client") {
        payload.clientUserIds = values.clientCheckbox
          ? values.clientUserIds
          : [];
      }
      if (values.type === "Inspector") {
        payload.inspectorUserIds = values.inspectorCheckbox
          ? values.inspectorUserIds
          : [];
      }
      editIssue(payload);
    },
  });

  useEffect(() => {
    dispatch(getInspectionFirmAllUsersSuccess({ response: null }));
    dispatch(getClientAllUsersSuccess({ response: null }));
    dispatch(getTenantAllUsersRequest());
  }, []); // eslint-disable-line

  useEffect(() => {
    if (orders) {
      const reducedOrders = orders.map((order: any) =>
        order?.orderNumber.toString()
      );
      setSelectedOrderNumber(reducedOrders);
      setSelectedOrders(orders);
    }
  }, [orders]);

  useEffect(() => {
    if (issueDetails) {
      const type = formik.values.type;
      if (type === "Client") {
        dispatch(getClientAllUsersRequest({ id: issueDetails?.client?.id }));
      }
      if (type === "Inspector") {
        dispatch(
          getInspectionFirmAllUsersRequest({
            id: issueDetails?.inspectorFirm?.id,
          })
        );
      }
    }
  }, [issueDetails]); // eslint-disable-line

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      if (searchText) {
        if (formik.values.type === "Client") {
          dispatch(
            getCreateIssueSearchOrderRequest({
              searchTerm: searchText.trim(),
              clientId: issueDetails?.client?.id,
            })
          );
        } else if (formik.values.type === "Inspector") {
          dispatch(
            getCreateIssueSearchOrderRequest({
              searchTerm: searchText.trim(),
              inspectorFirmId: issueDetails?.inspectorFirm?.id,
            })
          );
        } else {
          dispatch(
            getCreateIssueSearchOrderRequest({
              searchTerm: searchText.trim(),
            })
          );
        }
      } else {
        setOrderOptions([]);
      }
    }, 500);
    return () => clearTimeout(delayInputTimeoutId);
  }, [searchText]); // eslint-disable-line

  useEffect(() => {
    if (createIssueSearchResults) {
      const reducedOrders = createIssueSearchResults.map((order: any) =>
        order?.orderNumber.toString()
      );
      setOrderOptions(reducedOrders);
    }
  }, [createIssueSearchResults]);

  useEffect(() => {
    const orderIds = selectedOrders?.map((order) => order?.id);
    formik.setFieldValue("orderIds", orderIds);
  }, [selectedOrders]); // eslint-disable-line

  const getSubIssueTypes = () => {
    if (formik.values.type === "Tenant") {
      return [
        { label: "Internal", value: "Internal" },
        { label: "Payment", value: "Payment" },
        { label: "Qc", value: "Qc" },
      ];
    } else if (formik.values.type === "Client") {
      return [
        { label: "Contact", value: "Contact" },
        { label: "Recruiting", value: "Recruiting" },
        { label: "Update", value: "Update" },
      ];
    } else if (formik.values.type === "Inspector") {
      return [
        { label: "Contact", value: "InspectorContact" },
        { label: "Returned", value: "Returned" },
        { label: "Acceptance", value: "Acceptance" },
      ];
    }
  };

  const getTenantUserFullName = (id: string) => {
    const filterUser = tenantAllUsers?.filter((user: any) => user.id === id);
    return filterUser && filterUser.length > 0 && filterUser[0].fullName;
  };

  const deleteTenantUser = (id: string) => {
    const filteredUsers = formik.values.tenantUserIds.filter(
      (value: any) => value !== id
    );
    formik.setFieldValue("tenantUserIds", filteredUsers);
  };

  const getClientUserFullName = (id: string) => {
    const filterUser = clientAllUsers?.filter((user: any) => user.id === id);
    return filterUser && filterUser.length > 0 && filterUser[0].fullName;
  };

  const deleteClientUser = (id: string) => {
    const filteredUsers = formik.values.clientUserIds.filter(
      (value: string) => value !== id
    );
    formik.setFieldValue("clientUserIds", filteredUsers);
  };

  const getInspectorUserFullName = (id: string) => {
    const filterUser = inspectionFirmAllUsers?.filter(
      (user: any) => user.id === id
    );
    return filterUser && filterUser.length > 0 && filterUser[0].fullName;
  };

  const deleteInspectorUser = (id: string) => {
    const filteredUsers = formik.values.inspectorUserIds.filter(
      (value: string) => value !== id
    );
    formik.setFieldValue("inspectorUserIds", filteredUsers);
  };

  const editIssue = async (payload: any) => {
    setShowLoading(true);
    const response = await editIssueRequest(issueId, payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getIssueDetailsRequest({ issueId }));
      if (orderId) {
        dispatch(
          getOrderIssuesRequest({
            page: 1,
            pageSize: 10,
            orderId,
            sortColumn: "is_resolved",
            sortOrder: "asc",
          })
        );
        dispatch(getOrderIssuesCountRequest({ orderId }));
      } else {
        dispatch(
          getOrderIssuesRequest({
            page: 1,
            pageSize: 10,
            sortColumn: "is_resolved",
            sortOrder: "asc",
            ...(issueType ? { type: issueType } : {}),
          })
        );
      }
      onHide();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <form onSubmit={formik.submitForm}>
      <Box sx={{ border: "1px solid #C4C4C4", borderRadius: 1, padding: 1 }}>
        <Typography variant="h5" color="black">
          Issue
        </Typography>
        <Box className="display-name-layout">
          <Typography className="label-txt">Issue Type</Typography>
          <FormControl fullWidth disabled>
            <Select
              labelId="issue-type-label"
              id="issue-type"
              name="type"
              value={formik.values.type}
              onChange={(e) => {
                formik.handleChange(e);
                formik.setFieldValue("subType", "");
              }}
            >
              <MenuItem value={"Tenant"}>Internal Issue</MenuItem>
              <MenuItem value={"Client"}>Client Issue</MenuItem>
              <MenuItem value={"Inspector"}>Inspector Issue</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Issue Sub Type</Typography>
          <FormControl
            fullWidth
            disabled
            error={formik.touched.subType && Boolean(formik.errors.subType)}
          >
            <Select
              labelId="issue-subType-label"
              id="issue-subType"
              name="subType"
              value={formik.values.subType}
              onChange={formik.handleChange}
            >
              {getSubIssueTypes()?.map((subIssue) => (
                <MenuItem value={subIssue.value}>{subIssue.label}</MenuItem>
              ))}
            </Select>
            {formik.touched.subType && Boolean(formik.errors.subType) && (
              <FormHelperText>{formik.errors.subType as string}</FormHelperText>
            )}
          </FormControl>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Selected Orders</Typography>
          <FormControl
            fullWidth
            error={
              (formik.touched.orderIds || formik.submitCount > 0) &&
              Boolean(formik.errors.orderIds)
            }
          >
            <Autocomplete
              id="order-numbers"
              multiple
              disabled
              options={orderOptions}
              value={selectedOrderNumber}
              onChange={(event, newValue) => {
                setSelectedOrderNumber(newValue);
                const allOrders = [...orders, ...createIssueSearchResults];
                const filterOrderNumbers = newValue.map(Number);
                const filtered = allOrders.filter((order) =>
                  filterOrderNumbers.includes(order.orderNumber)
                );
                setSelectedOrders(filtered);
                setOrderOptions([]);
              }}
              freeSolo
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    sx={{ height: 28, borderWidth: 1.5 }}
                    variant="outlined"
                    label={option}
                    color="primary"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              )}
            />
            <FormHelperText>
              {(formik.touched.orderIds || formik.submitCount > 0) &&
                formik.errors.orderIds}
            </FormHelperText>
          </FormControl>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Box
            sx={{
              minWidth: 192,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Typography fontSize={12} color="rgba(0, 0, 0, 0.6)">
              Assigned Users
            </Typography>
            <Tooltip
              title="Assigned users will see this issue in their dashboard and issues queue. Assigned users will be able to resolve the issue and clear the flag."
              arrow
              placement="top"
            >
              <IconButton sx={{ p: 0, mr: 2 }}>
                <InfoOutlinedIcon color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box width={"100%"}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="tenantCheckbox"
                    checked={formik.values.tenantCheckbox}
                    onChange={formik.handleChange}
                  />
                }
                label="Internal Users"
                sx={{ width: 200 }}
              />
              <FormControl
                fullWidth
                error={
                  (formik.touched.tenantUserIds || formik.submitCount > 0) &&
                  Boolean(formik.errors.tenantUserIds)
                }
              >
                <Select
                  labelId="demo-multiple-chip-label"
                  id="demo-multiple-chip"
                  multiple
                  name="tenantUserIds"
                  value={formik.values.tenantUserIds}
                  onChange={formik.handleChange}
                  input={<OutlinedInput id="select-multiple-chip" />}
                  renderValue={(selected) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                      }}
                    >
                      {selected.map((value: any) => (
                        <Chip
                          key={value}
                          variant="outlined"
                          label={getTenantUserFullName(value)}
                          color="primary"
                          size="small"
                          onDelete={(e) => deleteTenantUser(value)}
                          deleteIcon={
                            <IconButton
                              onMouseDown={(e) => e.stopPropagation()}
                              size="small"
                              sx={{ padding: 0.5 }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          }
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {tenantAllUsers?.map((user: any) => (
                    <MenuItem
                      key={user.id}
                      value={user.id}
                      style={getStyles(
                        user.fullName,
                        formik.values.tenantUserIds,
                        theme
                      )}
                    >
                      {user.fullName}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {(formik.touched.tenantUserIds || formik.submitCount > 0) &&
                    (formik.errors.tenantUserIds as string)}
                </FormHelperText>
              </FormControl>
            </Box>

            {formik.values.type === "Client" && (
              <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="clientCheckbox"
                      checked={formik.values.clientCheckbox}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Client"
                  sx={{ width: 200 }}
                />
                <FormControl
                  fullWidth
                  error={
                    (formik.touched.clientUserIds || formik.submitCount > 0) &&
                    Boolean(formik.errors.clientUserIds)
                  }
                >
                  <Select
                    labelId="demo-multiple-chip-label"
                    id="demo-multiple-chip"
                    multiple
                    name="clientUserIds"
                    value={formik.values.clientUserIds}
                    onChange={formik.handleChange}
                    input={<OutlinedInput id="select-multiple-chip" />}
                    renderValue={(selected) => (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                        }}
                      >
                        {selected.map((value: string) => (
                          <Chip
                            key={value}
                            variant="outlined"
                            label={getClientUserFullName(value)}
                            color="primary"
                            size="small"
                            onDelete={(e) => deleteClientUser(value)}
                            deleteIcon={
                              <IconButton
                                onMouseDown={(e) => e.stopPropagation()}
                                size="small"
                                sx={{ padding: 0.5 }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            }
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {clientAllUsers?.map((user: any) => (
                      <MenuItem
                        key={user.id}
                        value={user.id}
                        style={getStyles(
                          user.fullName,
                          formik.values.tenantUserIds,
                          theme
                        )}
                      >
                        {user.fullName}
                      </MenuItem>
                    ))}
                  </Select>

                  <FormHelperText>
                    {(formik.touched.clientUserIds || formik.submitCount > 0) &&
                      (formik.errors.clientUserIds as string)}
                  </FormHelperText>
                </FormControl>
              </Box>
            )}
            {formik.values.type === "Inspector" && (
              <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="inspectorCheckbox"
                      checked={formik.values.inspectorCheckbox}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Inspector"
                  sx={{ width: 200 }}
                />
                <FormControl
                  fullWidth
                  error={
                    (formik.touched.inspectorUserIds ||
                      formik.submitCount > 0) &&
                    Boolean(formik.errors.inspectorUserIds)
                  }
                >
                  <Select
                    labelId="demo-multiple-chip-label"
                    id="demo-multiple-chip"
                    multiple
                    name="inspectorUserIds"
                    value={formik.values.inspectorUserIds}
                    onChange={formik.handleChange}
                    input={<OutlinedInput id="select-multiple-chip" />}
                    renderValue={(selected) => (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                        }}
                      >
                        {selected.map((value: string) => (
                          <Chip
                            key={value}
                            variant="outlined"
                            label={getInspectorUserFullName(value)}
                            color="primary"
                            size="small"
                            onDelete={(e) => deleteInspectorUser(value)}
                            deleteIcon={
                              <IconButton
                                onMouseDown={(e) => e.stopPropagation()}
                                size="small"
                                sx={{ padding: 0.5 }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            }
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {inspectionFirmAllUsers?.map((user: any) => (
                      <MenuItem
                        key={user.id}
                        value={user.id}
                        style={getStyles(
                          user.fullName,
                          formik.values.tenantUserIds,
                          theme
                        )}
                      >
                        {user.fullName}
                      </MenuItem>
                    ))}
                  </Select>

                  <FormHelperText>
                    {(formik.touched.inspectorUserIds ||
                      formik.submitCount > 0) &&
                      (formik.errors.inspectorUserIds as string)}
                  </FormHelperText>
                </FormControl>
              </Box>
            )}
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Issue</Typography>
          <TextField
            name="description"
            fullWidth
            disabled
            id="outlined-basic"
            label="Describe the Issue"
            variant="outlined"
            multiline
            onChange={formik.handleChange}
            value={formik.values.description}
            rows={2}
            error={
              (formik.touched.description || formik.submitCount > 0) &&
              Boolean(formik.errors.description)
            }
            helperText={
              (formik.touched.description || formik.submitCount > 0) &&
              (formik.errors.description as string)
            }
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Priority</Typography>
          <FormControl
            fullWidth
            error={formik.touched.priority && Boolean(formik.errors.priority)}
          >
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              name="priority"
              value={formik.values.priority}
              onChange={formik.handleChange}
            >
              <MenuItem value={"Low"}>Low</MenuItem>
              <MenuItem value={"Medium"}>Medium</MenuItem>
              <MenuItem value={"High"}>High</MenuItem>
            </Select>
            <FormHelperText>
              {formik.touched.priority && (formik.errors.priority as string)}
            </FormHelperText>
          </FormControl>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">
            Put Job on Hold Until <br /> Resolved?
          </Typography>
          <FormControlLabel
            control={
              <Switch
                name="onHoldUntilResolved"
                checked={formik.values.onHoldUntilResolved}
                onChange={formik.handleChange}
              />
            }
            label={formik.values.onHoldUntilResolved ? "Yes" : "No"}
          />
        </Box>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={onHide} type="reset">
            Cancel
          </Button>
          <Button
            sx={{ ml: 2 }}
            variant="contained"
            onClick={(e) => {
              formik.handleSubmit();
            }}
            disabled={!formik.isValid}
          >
            Save
          </Button>
        </Box>
      </Box>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {(loading || showLoading) && <Loading />}
    </form>
  );
}
